import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { authClient, useAuth } from "@/auth";
import { Button } from "@/components/catalyst/Button";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { ConfirmPasswordDialog } from "./ConfirmPasswordDialog";

const changePasswordSchema = z
	.object({
		newPassword: z.string().min(8, "Password must be at least 8 characters"),
		confirmPassword: z.string().min(1, "Confirm password is required"),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

type ChangePasswordData = z.infer<typeof changePasswordSchema>;

const changeEmailSchema = z.object({
	newEmail: z.string().email("Invalid email address"),
});

type ChangeEmailData = z.infer<typeof changeEmailSchema>;

export function AccountSettings() {
	const session = useAuth();
	const [confirmAction, setConfirmAction] = useState<{
		type: "password";
		data: ChangePasswordData;
	} | null>(null);

	// Password Form
	const {
		register: registerPassword,
		handleSubmit: handlePasswordSubmit,
		reset: resetPassword,
		formState: { errors: passwordErrors },
	} = useForm<ChangePasswordData>({
		resolver: zodResolver(changePasswordSchema),
	});

	const { mutate: changePassword, isPending: isChangingPassword } = useMutation(
		{
			mutationFn: async (args: {
				data: ChangePasswordData;
				currentPassword: string;
			}) => {
				const { error } = await authClient.changePassword({
					currentPassword: args.currentPassword,
					newPassword: args.data.newPassword,
					revokeOtherSessions: true,
				});
				if (error) throw error;
			},
			onSuccess: () => {
				toast.success("Password updated successfully");
				resetPassword();
				setConfirmAction(null);
			},
			onError: (error) => {
				toast.error(error.message || "Failed to update password");
			},
		},
	);

	// Email Form
	const {
		register: registerEmail,
		handleSubmit: handleEmailSubmit,
		reset: resetEmail,
		formState: { errors: emailErrors },
	} = useForm<ChangeEmailData>({
		resolver: zodResolver(changeEmailSchema),
		defaultValues: {
			newEmail: session?.user?.email ?? "",
		},
	});

	const { mutate: changeEmail, isPending: isChangingEmail } = useMutation({
		mutationFn: async (data: ChangeEmailData) => {
			await authClient.changeEmail(
				{ newEmail: data.newEmail },
				{ throw: true },
			);
		},
		onSuccess: () => {
			toast.success("Email update initiated. Please check your inbox.");
			resetEmail();
		},
		onError: (error) => {
			toast.error(error.message || "Failed to update email");
		},
	});

	const handleConfirm = (currentPassword: string) => {
		if (confirmAction?.type === "password") {
			changePassword({ data: confirmAction.data, currentPassword });
		}
	};

	const isPending = isChangingPassword || isChangingEmail;

	return (
		<div className="flex flex-col gap-8 max-w-2xl">
			{/* Change Password Section */}
			<section className="space-y-4">
				<h2 className="text-lg font-semibold text-base-content">
					Change Password
				</h2>
				<form
					onSubmit={handlePasswordSubmit((data) =>
						setConfirmAction({ type: "password", data }),
					)}
					className="space-y-4"
				>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<Field>
							<Label>New Password</Label>
							<Input
								type="password"
								{...registerPassword("newPassword")}
								autoComplete="new-password"
							/>
							{passwordErrors.newPassword && (
								<p className="text-sm text-error mt-1">
									{passwordErrors.newPassword.message}
								</p>
							)}
						</Field>

						<Field>
							<Label>Confirm Password</Label>
							<Input
								type="password"
								{...registerPassword("confirmPassword")}
								autoComplete="new-password"
							/>
							{passwordErrors.confirmPassword && (
								<p className="text-sm text-error mt-1">
									{passwordErrors.confirmPassword.message}
								</p>
							)}
						</Field>
					</div>
					<Button type="submit">Update Password</Button>
				</form>
			</section>

			<hr className="border-base-content/10" />

			{/* Change Email Section */}
			<section className="space-y-4">
				<h2 className="text-lg font-semibold text-base-content">
					Change Email
				</h2>
				<div className="text-sm text-base-content/70">
					Current email:{" "}
					<span className="font-medium text-base-content">
						{session?.user?.email}
					</span>
				</div>
				<form
					onSubmit={handleEmailSubmit((data) => changeEmail(data))}
					className="space-y-4"
				>
					<Field>
						<Label>New Email Address</Label>
						<Input type="email" {...registerEmail("newEmail")} />
						{emailErrors.newEmail && (
							<p className="text-sm text-error mt-1">
								{emailErrors.newEmail.message}
							</p>
						)}
					</Field>
					<Button type="submit">Update Email</Button>
				</form>
			</section>

			<ConfirmPasswordDialog
				isOpen={!!confirmAction}
				onClose={() => setConfirmAction(null)}
				onConfirm={handleConfirm}
				isPending={isPending}
				title={
					confirmAction?.type === "password"
						? "Confirm Password Change"
						: "Confirm Email Change"
				}
				description={
					confirmAction?.type === "password"
						? "Please enter your current password to confirm your password change."
						: "Please enter your password to confirm your email change."
				}
			/>
		</div>
	);
}
